/**
 * GraphQL documents for Phase 2 split search queries + overview stream.
 *
 * Three split queries (content, playlists, profiles) fire in parallel.
 * OVERVIEW_STREAM_SUBSCRIPTION subscribes to AI Service's oracleSearchOverviewStream
 * for the Sage narrative. Requires chatSessionId + intent from the aiSearch response.
 */

import { gql } from '@apollo/client'
import { PLAYLIST_ITEM_CONTENT_FIELDS } from '../fragments'

// ── Split queries (parallel from frontend) ─────────────────────────

export const SEARCH_QUERY = gql`
  query Search(
    $query: String
    $contentLimit: Int
    $contentOffset: Int
    $sessionId: ID
    $filters: SearchFilters
    $sort: SearchSort
    $skipOverview: Boolean
  ) {
    search(
      query: $query
      contentLimit: $contentLimit
      contentOffset: $contentOffset
      sessionId: $sessionId
      filters: $filters
      sort: $sort
      skipOverview: $skipOverview
    ) {
      content {
        id
        contentId
        contentType
        title
        score
        channel
        personalizationBoost
        publishedAtDate
        sortPopular
      }
      contentTotalCount
      topContentIds
      topContentScores
      entities {
        contentId
        contentType
        entityType
        name
        titleGroupId
        confidence
        matchType
      }
      intentSignal {
        classification
        reason
        requiresLlm
      }
      adjacency {
        centerId
        centerName
        sharedContentCount
        adjacencyType
      }
      chatSessionId
      blacklistMessage
    }
  }
`

// Backward compat alias
export { SEARCH_QUERY as AI_SEARCH_CONTENT_QUERY }

export const AI_SEARCH_PLAYLISTS_QUERY = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query AiSearchPlaylists(
    $query: String!
    $topContentIds: [Int!]!
    $topContentScores: [Float!]!
    $playlistLimit: Int
    $language: [String]
  ) {
    aiSearchPlaylists(
      query: $query
      topContentIds: $topContentIds
      topContentScores: $topContentScores
      playlistLimit: $playlistLimit
    ) {
      playlists {
        playlistId
        playlistName
        description
        itemCount
        matchedContentCount
        textRelevance
        score
        previewThumbnailUrls
        previewContentTitles
        creatorDisplayName
        playlist {
          id
          visibility
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
            avatar {
              url
              small
              card
            }
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
      }
      playlistTotalCount
    }
  }
`

export const AI_SEARCH_PROFILES_QUERY = gql`
  query AiSearchProfiles(
    $query: String!
    $profileLimit: Int
  ) {
    aiSearchProfiles(
      query: $query
      profileLimit: $profileLimit
    ) {
      uid
      bio
      location
      score
      userProfile {
        uid
        uuid
        displayName
        bio
        followerCount
        profileVisibility
        avatar {
          url
          small
          card
        }
        viewerContext {
          isFollowing
          isFollowedBy
        }
        topics {
          contentId
          title
        }
        topGenres(limit: 5) {
          name
          cleanName
        }
      }
    }
  }
`

// ── Tile hydration (separate query, cache-first for Apollo Client reuse) ──

export const SEARCH_TILE_HYDRATION_QUERY = gql`
  query SearchTileHydration(
    $titleGroupIds: [String]!
    $language: [String]!
    $imageType: [String]!
  ) {
    contentList(
      where: { titleGroupId: { in: $titleGroupIds } }
      language: $language
    ) {
      content {
        id
        contentId
        titleGroupId
        contentType
        title
        path
        shortDescription
        episodeNumber
        seasonNumber
        instructor
        displayType
        subType
        studio
        isNew
        featureMedia {
          duration
          mediaId
        }
        imageContentRelationships(
          language: $language
          types: $imageType
        ) {
          type
          image {
            url
          }
        }
        contentAssetReferences(contentAssetReferences: [{ type: "d6-keyart-no-text" }]) {
          asset {
            renderUrl
          }
          contentAssetReferenceType {
            type
          }
        }
        contentSeries(language: $language) {
          contentId
          title
          path
        }
        episodesCount
        seasonsCount
      }
    }
  }
`

// ── Overview stream subscription ────────────────────────────────────

export const OVERVIEW_STREAM_SUBSCRIPTION = gql`
  subscription SearchOverview($input: SearchOverviewStreamInput!) {
    oracleSearchOverviewStream(input: $input) {
      chatSessionId
      turnId
      textChunk
      statusText
      refinementSuggestions
      executionTime
      isFinal
      success
      errors
    }
  }
`
